import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, distinctUntilChanged, forkJoin, map, of, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AgentChatService } from '../agent/agent-chat.service';
import { AuthService } from '../services/auth.service';
import { OrganisationFeatures, OrganisationService } from '../services/organisation.service';
import {
  ACADEMY_CERTIFICATES,
  ACADEMY_LESSONS,
  ACADEMY_TOPICS,
  AcademyCertificate,
  AcademyCertificateId,
  AcademyLesson,
  AcademyTopicId,
  certificateById,
  NEW_LESSON_DAYS,
} from './academy-catalog';

/** Texte einer Lektion aus `assets/academy/<lang>.json`. */
export interface AcademyLessonText {
  title: string;
  summary: string;
  /** Ein Satz: welches Problem die Funktion löst (Lektionsmuster „Warum“). */
  why?: string;
  /** Untertitel je Filmszene. */
  scenes?: string[];
  /** Notiz je Schritt des Klickpfads — ein Satz, oder mit `tip` ein Zusatz für
   *  schwierige Schritte und andere Wege (erscheint kleiner darunter). */
  tour?: (string | AcademyTourText)[];
  points?: string[];
  quiz?: Record<string, { question: string; options: string[]; explanation?: string }>;
}

export interface AcademyTourText {
  text: string;
  tip?: string;
}

export interface AcademyContent {
  topics: Record<string, { title: string; description: string }>;
  certificates: Record<string, { title: string; description: string }>;
  news: Record<string, { title: string; text: string }>;
  lessons: Record<string, AcademyLessonText>;
}

/** Eine Quizfrage mit Text und Lösung — für `app-academy-quiz`. */
export interface AcademyQuestion {
  /** Eindeutig über alle Lektionen: `<lesson>.<frage>`. */
  key: string;
  lessonId: string;
  question: string;
  options: string[];
  correct: number;
  explanation?: string;
}

export type LessonStatus = 'not_started' | 'started' | 'completed';

export interface ProgressEntry {
  item_id: string;
  status: 'started' | 'completed' | 'passed' | 'failed';
  score?: number | null;
  content_version?: number | null;
  completed_at?: string | null;
  updated_at?: string | null;
}

export interface CertificateState {
  cert: AcademyCertificate;
  lessons: AcademyLesson[];
  completed: number;
  /** Abschlusstest bestanden. */
  earned: boolean;
  /** Alle Lektionen erledigt und Voraussetzung erfüllt. */
  examUnlocked: boolean;
  /** Fehlendes Vorgänger-Zertifikat. */
  missingPrerequisite?: AcademyCertificateId;
  bestScore: number | null;
}

const API = `${environment.tutorsBackend}/academy/progress`;
const LOCAL_KEY = 'al-academy-progress-v1';
/** Kein globaler Fehler-Toast (Fehler-Interceptor): ein Ausfall ist hier
 *  abgefangen — der Fortschritt läuft lokal weiter. Ohne das zeigte jedes
 *  Öffnen einer Lektion „404 Error“, solange ein Backend die Route noch
 *  nicht kennt (Staging vor dem Deploy, 29.10.2026). */
const QUIET = { headers: new HttpHeaders({ 'X-Suppress-Error-Toast': '1' }) };

/**
 * Academy: Inhalte (Katalog + Texte je Sprache) und der Lernfortschritt der
 * angemeldeten Person.
 *
 * Fortschritt: Quelle ist al-tutor (`/academy/progress`, Migration 244). Der
 * Browser hält eine Kopie je Person — fällt der Server aus (oder kennt die
 * Route noch nicht), lernt man weiter, und offene Einträge werden beim
 * nächsten erfolgreichen Laden nachgereicht. Ein Abschluss fällt nie zurück
 * (gleiche Regel wie `academy_ops.record`).
 */
@Injectable({ providedIn: 'root' })
export class AcademyService {
  readonly content$ = new BehaviorSubject<AcademyContent | null>(null);
  readonly progress$ = new BehaviorSubject<Map<string, ProgressEntry>>(new Map());
  /** `true`, sobald der Fortschritt einmal geladen ist (Server oder lokal). */
  readonly progressLoaded$ = new BehaviorSubject<boolean>(false);

  private contentLang: string | null = null;
  private loadedFor: string | null = null;
  /** Eine Seite will den Fortschritt — kommt das Token erst danach, wird nachgeladen. */
  private wanted = false;
  /** Server hat beim letzten Laden nicht geantwortet: nur lokal speichern,
   *  das nächste erfolgreiche Laden reicht alles nach. */
  private offline = false;
  private portalAgent = false;
  private creator = false;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private auth: AuthService,
    private agentChat: AgentChatService,
    private organisation: OrganisationService,
  ) {
    // Lektionen mit `when: 'portal-agent'` hängen am Status des Assistenten;
    // kommt er nach dem Fortschritt, bauen die Seiten über `progress$` neu.
    toObservable(this.agentChat.enabled)
      .pipe(distinctUntilChanged())
      .subscribe((on) => {
        this.portalAgent = on;
        this.progress$.next(new Map(this.progress$.value));
      });
    // `when: 'creator'`: dieselbe Bedingung wie der Menüpunkt „Creator“
    // (dashboard-layout) — Funktion der Organisation UND Zugriff der Person.
    this.organisation.organisation$
      .pipe(
        map(() => this.organisation.hasFeature(OrganisationFeatures.CONTENT_GENERATOR) && this.auth.hasFeatureAccess('CONTENT_GENERATOR')),
        distinctUntilChanged(),
      )
      .subscribe((on) => {
        this.creator = on;
        this.progress$.next(new Map(this.progress$.value));
      });
    this.translate.onLangChange.subscribe(() => this.ensureContent());
    this.auth.accessTokenPayload$
      .pipe(
        map((p) => p?.user_id ?? null),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        // Andere Person: nichts vom vorigen Stand zeigen.
        this.loadedFor = null;
        this.progress$.next(new Map());
        this.progressLoaded$.next(false);
        if (this.wanted) this.loadProgress();
      });
  }

  // ── Inhalte ──────────────────────────────────────────────────────────

  /** Lädt die Texte der aktuellen Sprache (Deutsch als Grundlage). */
  ensureContent(): void {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'de';
    if (this.contentLang === lang) return;
    this.contentLang = lang;
    const base$ = this.http.get<AcademyContent>('assets/academy/de.json');
    const lang$ =
      lang === 'de'
        ? of(null)
        : this.http.get<Partial<AcademyContent>>(`assets/academy/${lang}.json`).pipe(catchError(() => of(null)));
    forkJoin([base$, lang$])
      .pipe(take(1))
      .subscribe({
        next: ([base, local]) => {
          if (this.contentLang !== lang) return;
          this.content$.next(local ? deepMerge(base, local) : base);
        },
        error: () => (this.contentLang = null),
      });
  }

  lessonText(id: string): AcademyLessonText | null {
    return this.content$.value?.lessons[id] ?? null;
  }

  lessonTitle(id: string): string {
    return this.lessonText(id)?.title ?? id;
  }

  topicText(id: string): { title: string; description: string } {
    return this.content$.value?.topics[id] ?? { title: id, description: '' };
  }

  certificateText(id: string): { title: string; description: string } {
    return this.content$.value?.certificates[id] ?? { title: id, description: '' };
  }

  /** Quizfragen einer Lektion mit Text und Lösung (Fragen ohne Text fallen weg). */
  questionsOf(lesson: AcademyLesson): AcademyQuestion[] {
    const texts = this.lessonText(lesson.id)?.quiz ?? {};
    return (lesson.quiz ?? [])
      .filter((q) => texts[q.id]?.options?.length)
      .map((q) => ({
        key: `${lesson.id}.${q.id}`,
        lessonId: lesson.id,
        question: texts[q.id].question,
        options: texts[q.id].options,
        correct: q.correct,
        explanation: texts[q.id].explanation,
      }));
  }

  /** Fragen eines Abschlusstests: zufällig aus dem Pool der Prüfungsthemen. */
  drawExam(cert: AcademyCertificate): AcademyQuestion[] {
    const topics = cert.examTopics ?? cert.topics;
    const pool = this.lessons().filter((l) => topics.includes(l.topic)).flatMap((l) => this.questionsOf(l));
    return shuffle(pool).slice(0, cert.examSize);
  }

  /** Lektionen, die diese Person sehen soll (`AcademyLesson.when`). */
  lessons(): AcademyLesson[] {
    return ACADEMY_LESSONS.filter((l) => this.available(l));
  }

  lessonsOfTopic(topic: AcademyTopicId): AcademyLesson[] {
    return this.lessons().filter((l) => l.topic === topic);
  }

  /** Lektion nach ID — `undefined`, wenn es sie nicht gibt oder sie für diese Person aus ist. */
  lessonById(id: string): AcademyLesson | undefined {
    // Direkt aufgerufene Creator-Lektion: solange die Organisation noch lädt,
    // ist offen, ob es den Creator gibt — nicht vorschnell als „gibt es nicht“
    // behandeln (die Seite leitete sonst zur Startseite um).
    const loading = !this.organisation.organisation;
    return ACADEMY_LESSONS.find((l) => l.id === id && (this.available(l) || (loading && l.when === 'creator')));
  }

  private available(lesson: AcademyLesson): boolean {
    if (lesson.when === 'portal-agent') return this.portalAgent;
    if (lesson.when === 'creator') return this.creator;
    return true;
  }

  isNew(lesson: AcademyLesson, now = Date.now()): boolean {
    const added = Date.parse(lesson.addedAt);
    return !Number.isNaN(added) && now - added < NEW_LESSON_DAYS * 86_400_000;
  }

  /** Suche über Titel, Zusammenfassung und Kernpunkte (Groß/klein egal). */
  search(query: string): AcademyLesson[] {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return this.lessons();
    return this.lessons().filter((l) => {
      const t = this.lessonText(l.id);
      const topic = this.topicText(l.topic).title;
      const hay = [t?.title, t?.summary, ...(t?.points ?? []), topic].join(' ').toLocaleLowerCase();
      return hay.includes(q);
    });
  }

  // ── Fortschritt ─────────────────────────────────────────────────────

  /** Lädt den Fortschritt einmal je Person; `force` lädt neu. */
  loadProgress(force = false): void {
    this.wanted = true;
    const user = this.userId();
    if (!user || (!force && this.loadedFor === user)) return;
    this.loadedFor = user;
    const local = this.readLocal();
    this.http
      .get<{ items: ProgressEntry[] }>(API, QUIET)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (this.userId() !== user) return;
        this.offline = !res;
        if (!res) {
          // Server nicht erreichbar oder Route noch nicht da: lokal weiter.
          this.progress$.next(local);
          this.progressLoaded$.next(true);
          return;
        }
        const merged = new Map<string, ProgressEntry>();
        for (const e of res.items ?? []) merged.set(e.item_id, e);
        // Lokal Erreichtes, das der Server nicht kennt, nachreichen.
        for (const [id, e] of local) {
          const server = merged.get(id);
          if (!server || rank(e.status) > rank(server.status)) {
            merged.set(id, e);
            this.push(e);
          }
        }
        this.progress$.next(merged);
        this.writeLocal(merged);
        this.progressLoaded$.next(true);
      });
  }

  lessonStatus(id: string): LessonStatus {
    const e = this.progress$.value.get(`lesson:${id}`);
    if (!e) return 'not_started';
    return e.status === 'completed' ? 'completed' : 'started';
  }

  markStarted(id: string): void {
    if (this.lessonStatus(id) !== 'not_started') return;
    this.record({ item_id: `lesson:${id}`, status: 'started', content_version: this.lessonById(id)?.version });
  }

  markCompleted(id: string): void {
    if (this.lessonStatus(id) === 'completed') return;
    this.record({
      item_id: `lesson:${id}`,
      status: 'completed',
      content_version: this.lessonById(id)?.version,
      completed_at: new Date().toISOString(),
    });
  }

  recordExam(certId: AcademyCertificateId, score: number, passed: boolean): void {
    this.record({
      item_id: `exam:${certId}`,
      status: passed ? 'passed' : 'failed',
      score,
      completed_at: passed ? new Date().toISOString() : null,
    });
  }

  topicStats(topic: AcademyTopicId): { completed: number; started: number; total: number; percent: number } {
    const lessons = this.lessonsOfTopic(topic);
    const completed = lessons.filter((l) => this.lessonStatus(l.id) === 'completed').length;
    const started = lessons.filter((l) => this.lessonStatus(l.id) === 'started').length;
    return { completed, started, total: lessons.length, percent: pct(completed, lessons.length) };
  }

  overallStats(): { completed: number; started: number; open: number; total: number; percent: number } {
    const total = this.lessons().length;
    const completed = this.lessons().filter((l) => this.lessonStatus(l.id) === 'completed').length;
    const started = this.lessons().filter((l) => this.lessonStatus(l.id) === 'started').length;
    return { completed, started, open: total - completed - started, total, percent: pct(completed, total) };
  }

  /** Empfohlene nächste Lektion: zuletzt begonnene, sonst die erste offene in
   *  Katalog-Reihenfolge. `null`, wenn alles erledigt ist. */
  nextLesson(): AcademyLesson | null {
    const progress = this.progress$.value;
    const started = this.lessons().filter((l) => this.lessonStatus(l.id) === 'started').sort(
      (a, b) =>
        Date.parse(progress.get(`lesson:${b.id}`)?.updated_at ?? '') -
        Date.parse(progress.get(`lesson:${a.id}`)?.updated_at ?? ''),
    );
    return started[0] ?? this.lessons().find((l) => this.lessonStatus(l.id) === 'not_started') ?? null;
  }

  /** Nächste Lektion nach `id` im selben Thema, sonst die erste des nächsten Themas. */
  following(id: string): AcademyLesson | null {
    const lessons = this.lessons();
    const i = lessons.findIndex((l) => l.id === id);
    return i >= 0 ? (lessons[i + 1] ?? null) : null;
  }

  certificateState(id: AcademyCertificateId): CertificateState | null {
    const cert = certificateById(id);
    if (!cert) return null;
    const lessons = this.lessons().filter((l) => cert.topics.includes(l.topic));
    const completed = lessons.filter((l) => this.lessonStatus(l.id) === 'completed').length;
    const exam = this.progress$.value.get(`exam:${id}`);
    const earned = exam?.status === 'passed';
    const missingPrerequisite =
      cert.requires && this.progress$.value.get(`exam:${cert.requires}`)?.status !== 'passed'
        ? cert.requires
        : undefined;
    return {
      cert,
      lessons,
      completed,
      earned,
      examUnlocked: completed === lessons.length && !missingPrerequisite,
      missingPrerequisite,
      bestScore: exam?.score ?? null,
    };
  }

  certificates(): CertificateState[] {
    return ACADEMY_CERTIFICATES.map((c) => this.certificateState(c.id)!);
  }

  /** Themen mit mindestens einer sichtbaren Lektion — „Creator“ fehlt ohne Creator. */
  topics() {
    return ACADEMY_TOPICS.filter((t) => this.lessonsOfTopic(t.id).length > 0);
  }

  /** Lektionen, die mit einem Portalhinweis verknüpft sind. */
  hintLessons(): AcademyLesson[] {
    return this.lessons().filter((l) => !!l.hintId);
  }

  private record(entry: ProgressEntry): void {
    const map = new Map(this.progress$.value);
    const prev = map.get(entry.item_id);
    // Abgeschlossenes/Bestandenes fällt nie zurück, die beste Punktzahl bleibt.
    const next: ProgressEntry =
      prev && rank(prev.status) > rank(entry.status)
        ? { ...prev, score: best(prev.score, entry.score), updated_at: new Date().toISOString() }
        : { ...prev, ...entry, score: best(prev?.score, entry.score), updated_at: new Date().toISOString() };
    map.set(entry.item_id, next);
    this.progress$.next(map);
    this.writeLocal(map);
    this.push(entry);
  }

  private push(entry: ProgressEntry): void {
    if (this.offline) return;
    this.http
      .put(
        `${API}/${encodeURIComponent(entry.item_id)}`,
        {
          status: entry.status,
          score: entry.score ?? null,
          content_version: entry.content_version ?? null,
        },
        QUIET,
      )
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private userId(): string | null {
    return this.auth.accessTokenPayload$.value?.user_id ?? null;
  }

  private localKey(): string {
    return `${LOCAL_KEY}:${this.userId() ?? ''}`;
  }

  private readLocal(): Map<string, ProgressEntry> {
    try {
      const raw = localStorage.getItem(this.localKey());
      const list: ProgressEntry[] = raw ? JSON.parse(raw) : [];
      return new Map(Array.isArray(list) ? list.filter((e) => e?.item_id).map((e) => [e.item_id, e]) : []);
    } catch {
      return new Map();
    }
  }

  private writeLocal(map: Map<string, ProgressEntry>): void {
    try {
      localStorage.setItem(this.localKey(), JSON.stringify([...map.values()]));
    } catch {
      // Ohne Speicher zählt allein der Server.
    }
  }
}

/** Reihenfolge der Zustände — ein höherer überschreibt nie einen niedrigeren zurück. */
function rank(status: ProgressEntry['status']): number {
  return { started: 1, failed: 1, completed: 2, passed: 2 }[status] ?? 0;
}

function best(a?: number | null, b?: number | null): number | null {
  if (a == null) return b ?? null;
  if (b == null) return a;
  return Math.max(a, b);
}

function pct(done: number, total: number): number {
  return total ? Math.round((done / total) * 100) : 0;
}

function shuffle<T>(list: T[]): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Sprachdatei über die deutsche Grundlage legen — fehlende Texte bleiben deutsch. */
function deepMerge<T>(base: T, over: Partial<T>): T {
  if (Array.isArray(base) || Array.isArray(over) || typeof base !== 'object' || base === null) {
    return (over ?? base) as T;
  }
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(over as Record<string, unknown>)) {
    out[k] = v && typeof v === 'object' && !Array.isArray(v) ? deepMerge(out[k], v as never) : v;
  }
  return out as T;
}

